import { Capacitor } from "@capacitor/core";
import styled from "@emotion/styled";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { APIConfig, isAppVersionOutdated, isUserLocked, login, LoginData } from "../../../../API";
import { formatPhone } from "../../../../phone_number";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import AppModal from "../../../Generic/AppModal/AppModal";
import Flex from "../../../Generic/Flex";
import Spinner from "../../../Generic/Spinner";
import PinKeyboard from "../../../PinKeyboard";
import useModal from "../../../useModal";
import AuthForm, { AuthFormParagraph } from "../AuthForm";
import SMSCodeInput from "./SMSCodeInput";

interface Props {
  phoneNumber: string;
  apiConfig: APIConfig;
  onValidSubmit: (data: LoginData) => void;
  onVersionOutdated: () => void;
  onUserLocked: (phoneNumber: string) => void;
  onClickChangePhoneNumber: () => void;
}

const EnterSMSCodeStage = (props: Props) => {
  const modal = useModal();
  const [code, setCode] = React.useState("");

  const loginMutation = useMutation(
    ($code: string) =>
      login(
        props.apiConfig,
        props.phoneNumber,
        { isFromMobileApp: true, code: $code },
        Capacitor.getPlatform()
      ),
    {
      onError: (error) => {
        modal.setModalAsError({
          title: translate(STRINGS.ERROR),
          message: `${error}`,
        });
      },
      onSuccess: (response) => {
        if (isAppVersionOutdated(response)) {
          return props.onVersionOutdated();
        }

        if (isUserLocked(response)) {
          return props.onUserLocked(props.phoneNumber);
        }

        if (response === null) {
          setCode("");
          return modal.setModalAsError({
            title: translate(STRINGS.INCORRECT_SMS_CODE),
            message: translate(STRINGS.INCORRECT_SMS_CODE_EXPLAINED),
          });
        }

        props.onValidSubmit(response);
      },
    }
  );

  const handleChange = (newCode: string) => {
    if (/^\d{0,5}$/.test(newCode)) {
      setCode(newCode);
    }

    if (newCode.length === 5) {
      loginMutation.mutate(newCode);
    }
  };

  return (
    <EnterSMSCodeStage.Styled>
      <Flex direction="column" alignItems="center">
        <AuthFormParagraph>
          {translate(STRINGS.A_VERIFICATION_CODE_IS_BEING_SENT_TO)}{" "}
          <span style={{ whiteSpace: "nowrap" }}>
            <strong>{formatPhone(props.phoneNumber)}</strong>{" "}
            <LinkLike onClick={props.onClickChangePhoneNumber}>
              ({translate(STRINGS.CHANGE_PHONE_NUMBER)})
            </LinkLike>
          </span>
        </AuthFormParagraph>
      </Flex>

      <SMSCodeInput
        inputProps={{
          value: code,
          onChange: (e) => handleChange(e.target.value),
        }}
      />

      <EnterSMSCodeStage.KeyboardWrapper isLoading={loginMutation.isLoading}>
        <PinKeyboard value={code} onChange={handleChange} />
        <Spinner size={72} speed={0.5} color="var(--primary-color)" />
      </EnterSMSCodeStage.KeyboardWrapper>
      <AppModal {...modal.modalProps} />
    </EnterSMSCodeStage.Styled>
  );
};

const LinkLike = styled.span`
  color: var(--primary-color);
  font-weight: 700;
  cursor: pointer;
`;

EnterSMSCodeStage.Agreement = styled.div`
  display: flex;
  gap: 8px;
`;

EnterSMSCodeStage.KeyboardWrapper = styled.div<{ isLoading: boolean }>`
  position: relative;

  ${PinKeyboard.Root} {
    opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
    transition: opacity 0.2s;
  }

  ${Spinner.Styled} {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: auto;
    transition: opacity 0.2s;
    opacity: ${({ isLoading }) => (isLoading ? 1 : 0)};
    pointer-events: ${({ isLoading }) => (isLoading ? "all" : "none")};
  }
`;

EnterSMSCodeStage.Styled = styled(AuthForm)`
  ${PinKeyboard.Root} {
    margin: 32px 0;
  }

  ${SMSCodeInput.Root} {
    margin: 16px 0;
  }

  ${SMSCodeInput.Root}
`;

export default EnterSMSCodeStage;
